import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldPath,
  FormProvider,
  SubmitErrorHandler,
  type UseFormReturn,
  useForm,
  useWatch,
} from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import { get, isEqual, set } from 'lodash';
import {
  Building2,
  CircleDollarSignIcon,
  CircleUserRound,
  ExternalLinkIcon,
  ReceiptIcon,
  TruckIcon,
  Weight,
} from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useGuardedCreateLoad } from 'hooks/useGuardedCreateLoad';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { createLocation } from 'lib/api/createLocation';
import { getCustomers } from 'lib/api/getCustomers';
import { getLocations } from 'lib/api/getLocations';
import { getOperators } from 'lib/api/getOperators';
import { updateTMS } from 'lib/api/updateTMS';
import { FormStorageService } from 'lib/services/FormStorage/service';
import {
  LoadSectionAccordionItem,
  LoadTextInput,
} from 'pages/LoadView/LoadInformation/Components';
import { BillToSectionForm } from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/BillTo';
import { CustomerSectionForm } from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/Customer';
import { OperatorSectionForm } from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/Operator';
import { RatesForm } from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/Rates';
import { SpecificationsForm } from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/Specifications';
import { TurvoRouteSection } from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/TurvoRouteSection';
import {
  FuelSurchargeCostType,
  LinehaulCostType,
} from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/constants';
import {
  applySelectedLocationToTurvoRouteStop,
  ensureTurvoRouteStops,
  hydrateTurvoRouteEndsFromPickupConsignee,
  isTurvoDropoffStopType,
  isTurvoMultiStopRoute,
  mergeTurvoRouteEndsIntoPickupConsignee,
  normalizeTurvoRouteStopTypes,
  syncTurvoRouteStopsToPickupConsignee,
  validateTurvoMonotonicRouteStopDates,
} from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/turvoRouteStops';
import {
  buildTurvoLocationCacheEntryFromStop,
  buildTurvoLocationCreateOptions,
  hasValidAISuggestedLocation,
  normalizeTurvoTransportSizeForForm,
  normalizeTurvoTransportTypeForForm,
} from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/utils';
import LoadBuildingAccordionControls from 'pages/QuoteView/LoadBuilding/components/LoadBuildingAccordionControls';
import { LoadCreatedMessage } from 'pages/QuoteView/LoadBuilding/components/LoadCreatedMessage';
import { useLoadBuildingAccordionTabs } from 'pages/QuoteView/LoadBuilding/hooks/useLoadBuildingAccordionTabs';
import { useSharepointWriteback } from 'pages/QuoteView/LoadBuilding/sharepoint/hooks/useSharepointWriteback';
import { isSharepointSuggestion } from 'pages/QuoteView/LoadBuilding/sharepoint/utils/sharepointSuggestions';
import { suggestedTransportTypeOrEmpty } from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import {
  Load,
  LoadStopTypes,
  NormalizedLoad,
  RateData,
  Specifications,
  TMSCustomer,
  TMSLocation,
  Unit,
  createInitLoad,
  createInitRateData,
  createInitSpecs,
  normalizeLoad,
} from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMSOperation } from 'types/api/UpdateTMS';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import {
  type LoadBuildingSuggestions,
  type SuggestedLoad,
} from 'types/suggestions/LoadBuildingSuggestions';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import { captureResponseInPosthog } from 'utils/captureResponseInPosthog';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import { createLoadDateOnlyMetadata } from 'utils/dateOnlyFormValue';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  type ManualFieldError,
  applyManualValidationErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
  scheduleLoadBuildingSuggestionValidation,
} from 'utils/formValidation';
import {
  injectSelectedObject,
  normalizeToArray,
  normalizeToStringArray,
  withLoadBuildingSuggestionContext,
} from 'utils/loadInfoAndBuilding';
import { denormalizeDatesForTMSForm } from 'utils/parseDatesForTMSForm';

import DuplicateLoadWarningDialog from './DuplicateLoadWarningDialog';
import { validateDuplicateRouteStopLocations } from './utils/stopValidation';

enum AvailableTabs {
  customer = 'customer',
  billTo = 'billTo',
  specifications = 'specifications',
  route = 'route',
  carrier = 'carrier',
  operator = 'operator',
  rates = 'rates',
}

export type KeyValuePair = {
  key: string;
  value: string;
};

const initLoadBuildingForm = (tmsName: string): NormalizedLoad =>
  normalizeLoad(tmsName, {
    ...createInitLoad(),
    mode: 'TL',
    specifications: {
      ...createInitSpecs(),
      transportType: 'Van',
      serviceType: 'Any',
    },
    rateData: {
      ...createInitRateData(),
    },
  });

function customerExternalTMSIDString(
  customer: TMSCustomer | null | undefined
): string {
  const id = customer?.externalTMSID;
  if (id == null || id === '') {
    return '';
  }
  return String(id).trim();
}

const TURVO_CUSTOMER_HYDRATE_FIELDS = [
  'externalTMSID',
  'name',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'zipCode',
  'country',
] as const satisfies readonly (keyof TMSCustomer)[];

/** DebounceSelect often misses TMS customer fields after reset; set explicitly. */
function applyTurvoCustomerSelectHydrate(
  formMethods: UseFormReturn<NormalizedLoad>,
  customer: TMSCustomer | null | undefined
): boolean {
  const customerId = customerExternalTMSIDString(customer);
  if (!customerId) {
    return false;
  }

  const formCustomer = formMethods.getValues('customer');
  const formId = customerExternalTMSIDString(formCustomer);
  let changed = false;

  for (const field of TURVO_CUSTOMER_HYDRATE_FIELDS) {
    const from = customer?.[field];
    if (from == null || String(from).trim() === '') {
      continue;
    }
    const current = formCustomer?.[field];
    if (String(current ?? '').trim() === String(from).trim()) {
      continue;
    }
    if (field === 'externalTMSID' && formId === customerId) {
      continue;
    }
    formMethods.setValue(`customer.${field}`, from, {
      shouldDirty: false,
      shouldValidate: true,
    });
    changed = true;
  }

  return changed;
}

const initLoadEditForm = (load: Load): NormalizedLoad =>
  normalizeLoad(TMS.Turvo, load);

const hydrateTurvoCustomerLineItems = (rateData: RateData): RateData => {
  const existingLineItems = normalizeToArray(rateData.customerLineItems) || [];
  const synthesizedLineItems = [];

  const hasFuelSurchargeInLineItems = existingLineItems.some(
    (item) =>
      item?.label === FuelSurchargeCostType ||
      item?.label === 'Fuel - per mile' ||
      item?.label === 'Fuel - flat'
  );
  const hasLinehaulInLineItems = existingLineItems.some(
    (item) => item?.label === LinehaulCostType
  );

  const linehaulCharge = rateData.customerLineHaulCharge?.val ?? 0;
  const fscFlatRate = rateData.fscFlatRate ?? 0;

  let updatedCustomerLineHaulCharge = rateData.customerLineHaulCharge;
  let updatedCustomerLineHaulRate = rateData.customerLineHaulRate;
  let updatedFscFlatRate = rateData.fscFlatRate;

  if (linehaulCharge > 0 && !hasLinehaulInLineItems) {
    synthesizedLineItems.push({
      label: LinehaulCostType,
      unitBasis: 'Flat',
      quantity: 1,
      ratePerUnitUSD: linehaulCharge,
      totalChargeUSD: linehaulCharge,
      note: '',
    });
    updatedCustomerLineHaulCharge = {
      ...(updatedCustomerLineHaulCharge || {}),
      val: 0,
      unit: updatedCustomerLineHaulCharge?.unit ?? Unit.USD,
    };
    updatedCustomerLineHaulRate = null;
  }

  if (fscFlatRate > 0 && !hasFuelSurchargeInLineItems && !rateData.fscPerMile) {
    synthesizedLineItems.push({
      label: FuelSurchargeCostType,
      unitBasis: 'Flat',
      quantity: 1,
      ratePerUnitUSD: fscFlatRate,
      totalChargeUSD: fscFlatRate,
      note: '',
    });
    updatedFscFlatRate = 0;
  }

  // Fallback for older payloads that only include a total charge.
  const totalCharge = rateData.customerTotalCharge?.val ?? 0;
  if (
    synthesizedLineItems.length === 0 &&
    existingLineItems.length === 0 &&
    totalCharge > 0
  ) {
    synthesizedLineItems.push({
      label: LinehaulCostType,
      unitBasis: 'Flat',
      quantity: 1,
      ratePerUnitUSD: totalCharge,
      totalChargeUSD: totalCharge,
      note: '',
    });
  }
  const customerLineItems = [...synthesizedLineItems, ...existingLineItems];

  const customerTotalCharge =
    rateData.customerTotalCharge == null
      ? null
      : {
          val: rateData.customerTotalCharge.val,
          unit: rateData.customerTotalCharge.unit ?? Unit.USD,
        };

  const carrierTotalCost =
    rateData.carrierTotalCost == null
      ? null
      : {
          val: rateData.carrierTotalCost.val,
          unit: rateData.carrierTotalCost.unit ?? Unit.USD,
        };

  return {
    ...rateData,
    customerLineItems,
    customerLineHaulCharge: updatedCustomerLineHaulCharge,
    customerLineHaulRate: updatedCustomerLineHaulRate,
    customerTotalCharge,
    carrierTotalCost,
    fscFlatRate: updatedFscFlatRate,
  };
};

interface TurvoLoadFormProps {
  isCreateMode: boolean;
  load?: Maybe<Load>; // null for create mode, Load object for edit mode
  onSubmit?: (data: NormalizedLoad) => void; // Optional custom submit handler
  onInvalid?: SubmitErrorHandler<NormalizedLoad>;
  externalLinks?: any; // External links for edit mode
}

function flattenValues(
  values: any,
  parentKey = '',
  result: string[] = []
): string[] {
  for (const key in values) {
    const value = values[key];
    const newKey = parentKey ? `${parentKey}.${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenValues(value, newKey, result);
    } else {
      result.push(newKey);
    }
  }
  return result;
}

export default function TurvoLoadForm({
  isCreateMode,
  load,
  onSubmit,
  onInvalid,
  externalLinks,
}: TurvoLoadFormProps) {
  const { serviceID } = useServiceFeatures();
  const { tmsIntegrations } = useServiceFeatures();
  const { serviceFeaturesEnabled } = useServiceFeatures();
  const { toast } = useToast();
  const guardedCreateLoad = useGuardedCreateLoad();
  const tmsName = TMS.Turvo;
  const [isLoading, setIsLoading] = useState(false);
  // Only sections that are actually rendered (create hides billTo / carrier / operator).
  const showBillToSection = !isCreateMode;
  const showCarrierSection =
    !isCreateMode && serviceFeaturesEnabled.isCarrierVerificationEnabled;
  const showOperatorSection =
    !isCreateMode && serviceFeaturesEnabled.isOperatorEnabled;
  const allTabs = useMemo(() => {
    const tabs = [
      AvailableTabs.customer,
      AvailableTabs.specifications,
      AvailableTabs.rates,
      AvailableTabs.route,
    ];
    if (showCarrierSection) {
      tabs.push(AvailableTabs.carrier);
    }
    if (showOperatorSection) {
      tabs.push(AvailableTabs.operator);
    }
    if (showBillToSection) {
      tabs.push(AvailableTabs.billTo);
    }
    return tabs;
  }, [showBillToSection, showCarrierSection, showOperatorSection]);
  const {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  } = useLoadBuildingAccordionTabs({ allTabs });
  const [isLoadBuildingSuggestionClicked, setIsLoadBuildingSuggestionClicked] =
    useState(false);
  const [showAIHints, setShowAIHints] = useState(false);

  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  /** Raw list from GET /customers and customer search; never bind dropdown to this alone. */
  const [dataCustomers, setDataCustomers] =
    useState<Maybe<TMSCustomer[]>>(null);
  const [locations, setLocations] = useState<Maybe<TMSLocation[]>>(null);
  const [operators, setOperators] = useState<Array<string>>([]);
  const [tmsTenant, setTMSTenant] = useState<Maybe<string>>();
  const [builtLoad, setBuiltLoad] = useState<Maybe<Load>>(null);
  const {
    sharepointWriteStatus,
    resetWriteStatus,
    runSharepointWritebackAfterCreate,
  } = useSharepointWriteback();
  const [showDuplicateWarningDialog, setShowDuplicateWarningDialog] =
    useState(false);
  const [pendingSubmissionData, setPendingSubmissionData] =
    useState<Maybe<NormalizedLoad>>(null);
  /** Bumps only when `reset(memoizedDefaultValues)` runs so Radix Selects remount after hydrate, not on each user pick. */
  const [turvoSpecsSelectMountKey, setTurvoSpecsSelectMountKey] = useState(0);
  const [turvoCustomerSelectMountKey, setTurvoCustomerSelectMountKey] =
    useState(0);
  // SharePoint hydration can resolve a location before its option reaches the select.
  const [turvoLocationSelectMountKey, setTurvoLocationSelectMountKey] =
    useState(0);

  const posthog = usePostHog();

  const {
    currentState: {
      clickedSuggestion,
      threadId,
      emailId,
      threadItemId,
      goToSuggestion,
      openExternalUrl,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);
  const clickedSuggestionRef = useRef(clickedSuggestion);
  clickedSuggestionRef.current = clickedSuggestion;

  // Initialize form with appropriate default values
  const defaultValues = useMemo(() => {
    if (isCreateMode) {
      return initLoadBuildingForm(tmsName);
    } else if (load) {
      return initLoadEditForm(load);
    }
    return initLoadBuildingForm(tmsName);
  }, [isCreateMode, load, tmsName]);

  const formMethods = useForm<NormalizedLoad>({
    defaultValues,
  });

  const fetchCustomers = useCallback(async () => {
    setIsLoadingCustomers(true);

    const res = await getCustomers(tmsIntegrations[0]?.id);
    if (res.isOk()) {
      setDataCustomers(res.value.customerList);
      setTMSTenant(res.value.tmsTenant);
    } else {
      toast({
        description: res.error.message || 'Error while fetching customer list.',
        variant: 'destructive',
      });
    }
    setIsLoadingCustomers(false);
  }, [tmsIntegrations, toast]);

  const fetchLocations = useCallback(
    async (forceRefresh = false) => {
      setIsLoadingLocations(true);

      const res = await getLocations(tmsIntegrations[0]?.id, forceRefresh);
      if (res.isOk()) {
        const activeSuggestion = clickedSuggestionRef.current;
        setLocations(res.value.locationList);
        if (activeSuggestion && isSharepointSuggestion(activeSuggestion)) {
          setTurvoLocationSelectMountKey((key) => key + 1);
        }
      } else {
        const errorMessage = forceRefresh
          ? 'Error while refreshing location list.'
          : 'Error while fetching location list.';
        toast({
          description: res.error.message || errorMessage,
          variant: 'destructive',
        });
      }
      setIsLoadingLocations(false);
    },
    [tmsIntegrations, toast]
  );

  const fetchOperators = useCallback(async () => {
    if (!serviceFeaturesEnabled.isOperatorEnabled) {
      return;
    }

    const res = await getOperators(tmsIntegrations?.[0]?.id);
    if (res.isOk()) {
      setOperators(res.value);
    } else {
      toast({
        description: res.error.message || 'Error while fetching operator list.',
        variant: 'destructive',
      });
    }
  }, [serviceFeaturesEnabled.isOperatorEnabled, tmsIntegrations, toast]);

  const handleRefreshLocations = async () => {
    await fetchLocations(true);
  };

  // Initialize data on mount
  useEffect(() => {
    if (!tmsIntegrations?.length) {
      return;
    }

    fetchCustomers();
    fetchLocations();
    fetchOperators();
  }, [tmsIntegrations, fetchCustomers, fetchLocations, fetchOperators]);

  useEffect(() => {
    if (
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      setIsLoadBuildingSuggestionClicked(true);
    } else {
      setIsLoadBuildingSuggestionClicked(false);
    }
  }, [clickedSuggestion]);

  // Only clear the "Load Created" banner when switching suggestions;
  // keep it when the same suggestion is marked Accepted after building
  useEffect(() => {
    setBuiltLoad(null);
  }, [clickedSuggestion?.id]);

  /** Recompute hydrate when TMS match enriches the same Kit suggestion (id alone is insufficient). */
  const suggestionCustomerMergeKey = useMemo(() => {
    if (
      !isCreateMode ||
      clickedSuggestion?.pipeline !== SuggestionPipelines.LoadBuilding
    ) {
      return '';
    }
    const customer = (clickedSuggestion.suggested as SuggestedLoad)?.customer;
    return `${clickedSuggestion.id}:${customer?.externalTMSID ?? ''}:${customer?.name ?? ''}:${customer?.addressLine1 ?? ''}:${customer?.city ?? ''}:${customer?.state ?? ''}`;
  }, [isCreateMode, clickedSuggestion]);

  // Stable create-mode reset key: thread + suggestion id + customer enrichment.
  // Avoid depending on clickedSuggestion object identity — Kit/SharePoint rebuilds
  // replace the same card and would wipe in-progress edits via form reset.
  const createModeDefaultsKey = isCreateMode
    ? `${threadId ?? ''}|${clickedSuggestion?.id ?? ''}|${suggestionCustomerMergeKey}`
    : null;

  // Memoized default values with suggestion support for create mode
  const memoizedDefaultValues: NormalizedLoad = useMemo(() => {
    if (builtLoad) {
      const normalizedBuiltLoad = normalizeLoad(tmsName, builtLoad);
      const routeStops = hydrateTurvoRouteEndsFromPickupConsignee(
        ensureTurvoRouteStops({
          stops: normalizedBuiltLoad.stops,
          pickup: normalizedBuiltLoad.pickup,
          consignee: normalizedBuiltLoad.consignee,
        }),
        normalizedBuiltLoad.pickup,
        normalizedBuiltLoad.consignee
      );

      return {
        ...normalizedBuiltLoad,
        stops: routeStops,
        moreThanTwoStops: isTurvoMultiStopRoute(routeStops),
      };
    }

    const activeSuggestion = clickedSuggestionRef.current;
    if (
      isCreateMode &&
      createModeDefaultsKey != null &&
      activeSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      const castedClickedSuggestion =
        activeSuggestion.suggested as SuggestedLoad;

      const hydratedSuggestionRateData = hydrateTurvoCustomerLineItems({
        ...createInitRateData(),
        ...castedClickedSuggestion.rateData,
        customerTotalCharge: {
          ...castedClickedSuggestion.rateData.customerTotalCharge,
          unit:
            castedClickedSuggestion.rateData.customerTotalCharge?.unit ??
            Unit.USD,
        },
        carrierTotalCost: {
          ...castedClickedSuggestion.rateData.carrierTotalCost,
          unit:
            castedClickedSuggestion.rateData.carrierTotalCost?.unit ?? Unit.USD,
        },
      } as RateData);

      const suggestedFields = {
        ...castedClickedSuggestion,
        mode: 'TL',
        commodities: (() => {
          const comms = castedClickedSuggestion.commodities;
          return comms && typeof comms === 'object' && !Array.isArray(comms)
            ? Object.values(comms)
            : comms || [];
        })(),
        additionalReferences:
          normalizeToArray(castedClickedSuggestion.additionalReferences) || [],
        notes: normalizeToArray(castedClickedSuggestion.notes) || [],
        // Preserve refNumberCandidates fields
        customer: {
          ...castedClickedSuggestion.customer,
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.customer?.refNumberCandidates
          ),
        },
        pickup: {
          ...castedClickedSuggestion.pickup,
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.pickup?.refNumberCandidates
          ),
        },
        consignee: {
          ...castedClickedSuggestion.consignee,
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.consignee?.refNumberCandidates
          ),
        },
        specifications: {
          ...createInitSpecs(),
          ...castedClickedSuggestion.specifications,
          // Email named no equipment: leave it empty for the rep to pick.
          transportType: normalizeTurvoTransportTypeForForm(
            suggestedTransportTypeOrEmpty(
              castedClickedSuggestion.specifications
            )
          ),
          transportSize:
            normalizeTurvoTransportSizeForForm(
              castedClickedSuggestion.specifications?.transportSize
            ) || '',
        } as Specifications,
        rateData: {
          ...hydratedSuggestionRateData,
        } as RateData,
      };
      setShowAIHints(true);

      const res = {
        ...normalizeLoad(tmsName, createInitLoad()),
        ...suggestedFields,
        mode: 'TL',
        commodities: (() => {
          const comms = suggestedFields.commodities;
          return comms && typeof comms === 'object' && !Array.isArray(comms)
            ? Object.values(comms)
            : comms || [];
        })(),
        rateData: {
          ...suggestedFields.rateData,
          customerTotalCharge: {
            ...suggestedFields.rateData.customerTotalCharge,
            unit: Unit.USD,
          },
        },
      } as NormalizedLoad;

      const routeStops = ensureTurvoRouteStops({
        stops: res.stops,
        pickup: res.pickup,
        consignee: res.consignee,
      });
      const synced = syncTurvoRouteStopsToPickupConsignee(routeStops);
      return {
        ...res,
        stops: routeStops,
        pickup: synced.pickup,
        consignee: synced.consignee,
        moreThanTwoStops: isTurvoMultiStopRoute(routeStops),
      };
    }

    // Handle edit mode with existing load data
    if (!isCreateMode && load) {
      const normalizedLoad = normalizeLoad(tmsName, load);
      normalizedLoad.rateData = hydrateTurvoCustomerLineItems(
        normalizedLoad.rateData
      );

      const routeStops = hydrateTurvoRouteEndsFromPickupConsignee(
        ensureTurvoRouteStops({
          stops: normalizedLoad.stops,
          pickup: normalizedLoad.pickup,
          consignee: normalizedLoad.consignee,
        }),
        normalizedLoad.pickup,
        normalizedLoad.consignee
      );

      return {
        ...normalizedLoad,
        stops: routeStops,
        moreThanTwoStops: isTurvoMultiStopRoute(routeStops),
      };
    }

    setShowAIHints(false);

    const init = initLoadBuildingForm(tmsName);
    const routeStops = ensureTurvoRouteStops({
      stops: init.stops,
      pickup: init.pickup,
      consignee: init.consignee,
    });
    const synced = syncTurvoRouteStopsToPickupConsignee(routeStops);
    return {
      ...init,
      stops: routeStops,
      pickup: synced.pickup,
      consignee: synced.consignee,
      moreThanTwoStops: false,
    };
  }, [builtLoad, isCreateMode, load, tmsName, createModeDefaultsKey]);

  useEffect(() => {
    if (!memoizedDefaultValues) {
      return;
    }
    formMethods.reset(memoizedDefaultValues, {
      keepDefaultValues: false,
      keepTouched: false,
      keepDirtyValues: false,
      keepDirty: false,
    });
    setTurvoSpecsSelectMountKey((k) => k + 1);
    applyTurvoCustomerSelectHydrate(
      formMethods,
      memoizedDefaultValues.customer
    );
    setTurvoCustomerSelectMountKey((k) => k + 1);

    const activeSuggestion = clickedSuggestionRef.current;
    if (activeSuggestion && isSharepointSuggestion(activeSuggestion)) {
      setTurvoLocationSelectMountKey((key) => key + 1);
    }

    // Surface missing required fields after AI hydrate (no submit).
    return scheduleLoadBuildingSuggestionValidation(
      Boolean(
        isCreateMode &&
        !builtLoad &&
        activeSuggestion &&
        activeSuggestion.pipeline === SuggestionPipelines.LoadBuilding
      ),
      () => {
        void formMethods.trigger();
      }
    );
  }, [memoizedDefaultValues, formMethods, isCreateMode, builtLoad]);

  const watchedRouteStops = useWatch({
    control: formMethods.control,
    name: 'stops',
  });

  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion,
    load?.freightTrackingID,
    !isCreateMode,
    memoizedDefaultValues,
    watchedRouteStops?.length ?? 0
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    Object.values(AvailableTabs)
  );

  const watchedCustomerForMerge = useWatch({
    control: formMethods.control,
    name: 'customer',
  });

  const originalSuggestionCustomer = useMemo(() => {
    if (clickedSuggestion?.pipeline !== SuggestionPipelines.LoadBuilding) {
      return null;
    }
    return (clickedSuggestion.suggested as SuggestedLoad)?.customer ?? null;
  }, [clickedSuggestion]);

  const prevMergedCustomersRef = useRef<TMSCustomer[]>([]);

  /**
   * Merge raw API/search results with AI-suggested and current form customers so
   * customer search (which replaces `dataCustomers`) cannot drop them from the dropdown.
   */
  const mergedCustomers = useMemo(() => {
    let base = [...(dataCustomers ?? [])];

    if (originalSuggestionCustomer?.externalTMSID?.trim()) {
      const suggested = originalSuggestionCustomer as TMSCustomer;
      if (suggested?.name || suggested?.addressLine1) {
        base = injectSelectedObject(suggested, base);
        const suggestedId = String(suggested.externalTMSID).trim();
        const idx = base.findIndex(
          (c) =>
            c.externalTMSID != null &&
            String(c.externalTMSID).trim() === suggestedId
        );
        if (idx >= 0) {
          const merged = { ...base[idx], ...suggested };
          if (!isEqual(merged, base[idx])) {
            base = base.slice();
            base[idx] = merged;
          }
        }
      }
    }

    if (watchedCustomerForMerge?.externalTMSID?.trim()) {
      const formCustomer = watchedCustomerForMerge as TMSCustomer;
      if (formCustomer?.name || formCustomer?.addressLine1) {
        base = injectSelectedObject(formCustomer, base);
      }
    }

    if (isEqual(base, prevMergedCustomersRef.current)) {
      return prevMergedCustomersRef.current;
    }

    prevMergedCustomersRef.current = base;
    return base;
  }, [dataCustomers, originalSuggestionCustomer, watchedCustomerForMerge]);

  const setCustomersAction = useCallback<
    React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>
  >((customersUpdate) => {
    if (typeof customersUpdate === 'function') {
      setDataCustomers((prev) => {
        const next = customersUpdate(prev);
        if (isEqual(next, prev)) {
          return prev;
        }
        return next ?? [];
      });
    } else {
      const customersArray = customersUpdate ?? [];
      setDataCustomers((prev) => {
        if (isEqual(customersArray, prev)) {
          return prev;
        }
        return customersArray;
      });
    }
  }, []);

  // When SharePoint re-sync enriches the same card with TMS customer id, re-apply if reset missed it.
  useEffect(() => {
    if (!isCreateMode) {
      return;
    }
    if (
      !applyTurvoCustomerSelectHydrate(formMethods, originalSuggestionCustomer)
    ) {
      return;
    }
    setTurvoCustomerSelectMountKey((k) => k + 1);
  }, [
    isCreateMode,
    suggestionCustomerMergeKey,
    originalSuggestionCustomer,
    formMethods,
  ]);

  useEffect(() => {
    const locationsReady = !isLoadingLocations && locations != null;
    if (!locationsReady || !memoizedDefaultValues) {
      return;
    }

    const additionalLocations: TMSLocation[] = [];
    if (memoizedDefaultValues.pickup?.externalTMSID) {
      additionalLocations.push(
        buildTurvoLocationCacheEntryFromStop(memoizedDefaultValues.pickup)
      );
    }
    if (memoizedDefaultValues.consignee?.externalTMSID) {
      additionalLocations.push(
        buildTurvoLocationCacheEntryFromStop(memoizedDefaultValues.consignee)
      );
    }

    if (additionalLocations.length > 0) {
      setLocations((prevLocations) => {
        let updatedLocations = prevLocations ?? [];
        additionalLocations.forEach((loc) => {
          updatedLocations = injectSelectedObject(loc, updatedLocations);
        });
        return updatedLocations;
      });
    }
  }, [isLoadingLocations, locations, memoizedDefaultValues]);

  const { formState } = formMethods;

  // Clear form functionality
  function handleClearForm() {
    setShowAIHints(false);
    setBuiltLoad(null);
    resetWriteStatus();
    setPendingSubmissionData(null);
    setShowDuplicateWarningDialog(false);
    setIsLoading(false); // Reset loading state when clearing form

    FormStorageService.clearFormState(`${tmsName}_${threadId}`);

    // Deselect the Kit suggestion
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: null,
    }));

    // Reset form to empty values directly - bypass all memoization
    const emptyForm = initLoadBuildingForm(tmsName);
    formMethods.reset(emptyForm, {
      keepDefaultValues: false,
      keepDirty: false,
      keepDirtyValues: false,
      keepTouched: false,
    });

    // Clear any validation errors
    formMethods.clearErrors();
  }

  // Form state persistence for create mode
  useEffect(() => {
    if (isCreateMode && threadId) {
      const savedState = FormStorageService.getFormState<
        NormalizedLoad,
        NormalizedLoad
      >(`${tmsName}_${threadId}`);

      if (
        savedState &&
        savedState.threadID === threadId &&
        clickedSuggestion == null
      ) {
        setShowAIHints(true);
        goToSuggestion({
          suggestionID: savedState.clickedSuggestion?.id,
        });

        // Flatten savedState.values to get all field names
        const fieldNames = flattenValues(savedState.values);
        const cleanFields = normalizeLoad(tmsName, createInitLoad());

        // For each field, set the value and mark as dirty if necessary
        fieldNames.forEach((fieldName) => {
          const isDirty: Undef<boolean> = get(
            savedState.dirtyFields,
            fieldName
          );
          if (isDirty === undefined || !isDirty) {
            set(cleanFields, fieldName, get(savedState.values, fieldName));
          }
        });

        formMethods.reset(cleanFields as NormalizedLoad, {
          keepDefaultValues: false,
          keepTouched: false,
          keepDirtyValues: false,
          keepDirty: false,
        });

        setTimeout(() => {
          fieldNames.forEach((fieldName) => {
            const isDirty: Undef<boolean> = get(
              savedState.dirtyFields,
              fieldName
            );

            if (isDirty) {
              formMethods.setValue(
                fieldName as FieldPath<NormalizedLoad>,
                get(savedState.values, fieldName),
                {
                  shouldDirty: isDirty,
                  shouldTouch: true,
                }
              );
            }
          });

          // After resetting the form, ensure that options arrays include the necessary data
          const customerID = savedState.values.customer?.externalTMSID;
          if (customerID) {
            // Customer row is merged into `mergedCustomers` via useWatch after setValue runs.
          }

          const additionalLocations: TMSLocation[] = [];
          if (savedState.values.pickup?.externalTMSID) {
            additionalLocations.push(
              buildTurvoLocationCacheEntryFromStop(savedState.values.pickup)
            );
          }

          if (savedState.values.consignee?.externalTMSID) {
            additionalLocations.push(
              buildTurvoLocationCacheEntryFromStop(savedState.values.consignee)
            );
          }

          setLocations((prevLocations) => {
            let updatedLocations = prevLocations ?? [];

            additionalLocations.forEach((loc) => {
              updatedLocations = injectSelectedObject(loc, updatedLocations);
            });

            return updatedLocations;
          });
        }, 100);
      }

      // Wait for RHF to finish rendering before caching initial form state
      setTimeout(() => {
        if (!savedState || threadId === savedState.threadID) {
          FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
            threadID: threadId,
            clickedSuggestion: isLoadBuildingSuggestionClicked
              ? clickedSuggestion
              : null,
            values: formMethods.getValues(),
            dirtyFields: formMethods.formState.dirtyFields,
          });
        }
      }, 100);

      // Subscribe to form to cache state on changes
      const subscription = formMethods.watch((value) => {
        if (!savedState || threadId === savedState.threadID) {
          FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
            threadID: threadId,
            values: value,
            clickedSuggestion: isLoadBuildingSuggestionClicked
              ? clickedSuggestion
              : null,
            dirtyFields: formMethods.formState.dirtyFields,
          });
        }
      });

      return () => {
        subscription.unsubscribe();
      };
    }
    return undefined;
  }, [
    formMethods,
    threadId,
    isLoadBuildingSuggestionClicked,
    clickedSuggestion,
    tmsName,
    isCreateMode,
    goToSuggestion,
  ]);

  /**
   * Creates AI-suggested locations that haven't been manually selected.
   * Returns updated form data with location IDs set, or null if creation failed.
   */
  const createAISuggestedLocations = async (
    formData: NormalizedLoad
  ): Promise<Maybe<NormalizedLoad>> => {
    const originalSuggestionData =
      clickedSuggestion?.suggested as Undef<NormalizedLoad>;

    if (!originalSuggestionData) {
      return formData;
    }

    const stopsToCheck: LoadStopTypes[] = [
      LoadStopTypes.Pickup,
      LoadStopTypes.Consignee,
    ];
    const updatedData = { ...formData };
    const createdLocations: Array<{
      location: TMSLocation;
      stopType: LoadStopTypes;
    }> = [];

    for (const stop of stopsToCheck) {
      if (
        hasValidAISuggestedLocation(stop, updatedData, originalSuggestionData)
      ) {
        const stopData = originalSuggestionData[stop];
        if (!stopData) {
          continue;
        }

        const locationPayload: TMSLocation = {
          ID: undefined,
          externalTMSID: '',
          name: stopData.name || '',
          addressLine1: stopData.addressLine1 || '',
          addressLine2: stopData.addressLine2 || '',
          city: stopData.city || '',
          state: stopData.state || '',
          zipCode: stopData.zipCode || '',
          country: stopData.country || 'United States',
          contact: '',
          phone: stopData.phone || '',
          email: stopData.email || '',
          apptRequired: false,
          isShipper: stop === LoadStopTypes.Pickup,
          isConsignee: stop === LoadStopTypes.Consignee,
        };

        const options = buildTurvoLocationCreateOptions({
          phone: stopData.phone,
          specialInstructions: stopData.instructions,
        });

        const res = await createLocation({
          location: locationPayload,
          tmsID: tmsIntegrations[0]?.id,
          ...(options && { options }),
        });

        if (res.isOk()) {
          const newLocation = res.value.location;
          createdLocations.push({ location: newLocation, stopType: stop });

          if (updatedData[stop]) {
            (updatedData[stop] as any).externalTMSID =
              newLocation.externalTMSID;
          }

          setLocations((prevLocations) => {
            const updatedLocations = prevLocations
              ? [...prevLocations, newLocation]
              : [newLocation];
            return updatedLocations;
          });
        } else {
          toast({
            description:
              res.error.message ||
              `Error creating ${stop} location. Please try again.`,
            variant: 'destructive',
          });
          return null;
        }
      }
    }

    // Update form values with the new location IDs. The route section owns the
    // stops the load is built from, so each new location also has to land on the
    // matching route end.
    if (createdLocations.length > 0) {
      const routeStops = [...(updatedData.stops ?? [])];

      createdLocations.forEach(({ location, stopType }) => {
        formMethods.setValue(
          `${stopType}.externalTMSID` as FieldPath<NormalizedLoad>,
          location.externalTMSID,
          {
            shouldDirty: true,
          }
        );

        const stopIndex =
          stopType === LoadStopTypes.Pickup ? 0 : routeStops.length - 1;
        if (!routeStops[stopIndex]) {
          return;
        }

        routeStops[stopIndex] = applySelectedLocationToTurvoRouteStop(
          routeStops[stopIndex],
          location
        );
        formMethods.setValue(
          `stops.${stopIndex}` as FieldPath<NormalizedLoad>,
          routeStops[stopIndex],
          { shouldDirty: true }
        );
      });

      updatedData.stops = routeStops;
    }

    return updatedData;
  };

  const processFormSubmit = async (
    data: NormalizedLoad,
    options: { bypassDuplicateCheck?: boolean } = {}
  ) => {
    const { bypassDuplicateCheck = false } = options;

    setIsLoading(true);

    const routeStops = normalizeTurvoRouteStopTypes(
      ensureTurvoRouteStops(data)
    );
    // Create builds the ends from the route; update keeps the saved location
    // details Turvo's route payload omits. The ends must be in sync before
    // creating AI locations, otherwise a stop the user already picked looks
    // unresolved and gets duplicated in the TMS.
    const routeEnds = isCreateMode
      ? syncTurvoRouteStopsToPickupConsignee(routeStops)
      : mergeTurvoRouteEndsIntoPickupConsignee(
          routeStops,
          data.pickup,
          data.consignee
        );

    // Auto-create AI-suggested locations before submission
    const submissionData = await createAISuggestedLocations({
      ...data,
      stops: routeStops,
      pickup: routeEnds.pickup,
      consignee: routeEnds.consignee,
      moreThanTwoStops: isTurvoMultiStopRoute(routeStops),
    });
    if (submissionData === null) {
      setIsLoading(false);
      return;
    }

    const normalizedRouteStops = submissionData.stops ?? [];

    if (isCreateMode && builtLoad && !bypassDuplicateCheck) {
      setIsLoading(false); // Reset loading state so user can interact with dialog
      setPendingSubmissionData(submissionData);
      setShowDuplicateWarningDialog(true);
      return; // let the dialog handle the confirmation logic
    }

    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) =>
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.Turvo,
          activeTabs,
          setActiveTabs,
          toast,
          afterErrorHandled: () => setIsLoading(false),
          routeStopCount: normalizedRouteStops.length,
        })
      );

    // Drop stale manual errors from a prior submit; RHF field rules already passed.
    formMethods.clearErrors();

    if (normalizedRouteStops.length < 2) {
      applySubmitValidationErrors([
        {
          path: 'stops.0.externalTMSID',
          message: 'Please provide at least a pickup and a consignee.',
        },
      ]);
      return;
    }

    for (let index = 0; index < normalizedRouteStops.length; index++) {
      const stop = normalizedRouteStops[index];
      if (!stop.externalTMSID?.trim()) {
        applySubmitValidationErrors([
          {
            path: `stops.${index}.externalTMSID` as FieldPath<NormalizedLoad>,
            message: 'Location is required for each stop.',
          },
        ]);
        return;
      }

      const hasDate = isTurvoDropoffStopType(stop.stopType)
        ? Boolean(stop.mustDeliver)
        : Boolean(stop.readyTime);
      if (!hasDate) {
        applySubmitValidationErrors([
          {
            path: (isTurvoDropoffStopType(stop.stopType)
              ? `stops.${index}.mustDeliver`
              : `stops.${index}.readyTime`) as FieldPath<NormalizedLoad>,
            message: isTurvoDropoffStopType(stop.stopType)
              ? 'Delivery date is required.'
              : 'Pickup date is required.',
          },
        ]);
        return;
      }

      if (stop.apptStartTime && stop.apptEndTime) {
        const start = new Date(String(stop.apptStartTime));
        const end = new Date(String(stop.apptEndTime));
        if (
          !Number.isNaN(start.getTime()) &&
          !Number.isNaN(end.getTime()) &&
          start > end
        ) {
          applySubmitValidationErrors([
            {
              path: `stops.${index}.apptEndTime` as FieldPath<NormalizedLoad>,
              message:
                'Appointment Start Time cannot be later than Appointment End Time',
            },
          ]);
          return;
        }
      }
    }

    const duplicateStops =
      validateDuplicateRouteStopLocations(normalizedRouteStops);
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        {
          path: `stops.${duplicateStops.pathIndex}.externalTMSID` as FieldPath<NormalizedLoad>,
          message: duplicateStops.message,
        },
      ]);
      return;
    }

    const monotonicStops =
      validateTurvoMonotonicRouteStopDates(normalizedRouteStops);
    if (!monotonicStops.ok) {
      const nextStop = normalizedRouteStops[monotonicStops.pathIndex];
      applySubmitValidationErrors([
        {
          path: (isTurvoDropoffStopType(nextStop?.stopType)
            ? `stops.${monotonicStops.pathIndex}.mustDeliver`
            : `stops.${monotonicStops.pathIndex}.readyTime`) as FieldPath<NormalizedLoad>,
          message: monotonicStops.message,
        },
      ]);
      return;
    }

    if (isCreateMode) {
      // Create mode - build new load
      try {
        const activeLoadBuildingSuggestion =
          clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
            ? (clickedSuggestion as LoadBuildingSuggestions)
            : undefined;

        const reqData = withLoadBuildingSuggestionContext(
          {
            clickedSuggestion: activeLoadBuildingSuggestion,
            threadId,
            emailId,
            emailExternalId: threadItemId, // pass threadItemId from sidebar state as emailExternalId
          },
          {
            load: {
              tmsID: tmsIntegrations[0]?.id,
              mode: submissionData.mode,
              poNums: submissionData.poNums,
              moreThanTwoStops: submissionData.moreThanTwoStops,
              stops: (submissionData.stops ?? []).map((stop) => {
                const denormalizedTimes = denormalizeDatesForTMSForm(tmsName, {
                  readyTime: stop.readyTime,
                  readyEndTime: stop.readyEndTime,
                  mustDeliver: stop.mustDeliver,
                  apptStartTime: stop.apptStartTime,
                  apptEndTime: stop.apptEndTime,
                });
                return {
                  ...stop,
                  ...denormalizedTimes,
                };
              }),
              customer: denormalizeDatesForTMSForm(
                tmsName,
                submissionData.customer
              ),
              specifications: submissionData.specifications,
              pickup: {
                ...denormalizeDatesForTMSForm(tmsName, submissionData.pickup),
              },
              consignee: {
                ...denormalizeDatesForTMSForm(
                  tmsName,
                  submissionData.consignee
                ),
              },
              rateData: denormalizeDatesForTMSForm(
                tmsName,
                submissionData.rateData
              ),
              commodities: submissionData.commodities,
            } as unknown as Load,
            metadata: createLoadDateOnlyMetadata(
              submissionData.pickup.readyTime,
              submissionData.consignee.mustDeliver
            ),
          }
        );

        const res = await guardedCreateLoad(
          submissionData.customer,
          reqData,
          mergedCustomers
        );
        if (!res) {
          setIsLoading(false);
          return;
        }

        if (res.isOk()) {
          setBuiltLoad(res.value.load);

          const loadIdStr =
            res.value.load?.freightTrackingID?.trim() ||
            res.value.load?.externalTMSID?.trim() ||
            '';
          void runSharepointWritebackAfterCreate({
            loadId: loadIdStr,
            activeLoadBuildingSuggestion,
          });

          FormStorageService.clearFormState(`${tmsName}_${threadId}`);

          if (clickedSuggestion) {
            setCurrentState((prevState) => ({
              ...prevState,
              ...applyCompleteKitSuggestions(
                prevState,
                clickedSuggestion.id,
                SuggestionPipelines.LoadBuilding
              ),
            }));
          }
        } else {
          toast({
            description: res.error.message || 'Error building load.',
            variant: 'destructive',
          });
        }
        captureResponseInPosthog(posthog, !res.isOk(), {
          serviceID,
          tmsName,
          submissionType: isCreateMode
            ? ButtonNamePosthog.BuildLoad
            : ButtonNamePosthog.UpdateTMS,
        });
      } catch {
        toast({
          description: 'Error building load.',
          variant: 'destructive',
        });
      }
      setIsLoading(false);
    } else {
      // Edit mode - update existing load
      try {
        const denormalizedData = denormalizeDatesForTMSForm(tmsName, {
          ...submissionData,
          // Nested stop dates are not covered by the top-level denormalize.
          stops: (submissionData.stops ?? []).map((stop) => ({
            ...stop,
            ...denormalizeDatesForTMSForm(tmsName, stop),
          })),
        });

        // Define proper interface for update payload
        interface UpdatePayload extends Pick<
          Load,
          | 'customer'
          | 'billTo'
          | 'pickup'
          | 'consignee'
          | 'carrier'
          | 'rateData'
        > {
          operator: string;
          freightTrackingID: string;
        }

        // Create update data without carrier to avoid time field validation issues
        const updateData: UpdatePayload = {
          ...denormalizedData,
          operator: submissionData.operator || '',
          freightTrackingID: submissionData.freightTrackingID || '',
        } as UpdatePayload;

        // Strip carrier unless applying a Carrier Info Kit suggestion
        if (
          'carrier' in updateData &&
          clickedSuggestion?.pipeline !== SuggestionPipelines.CarrierInfo
        ) {
          delete (updateData as any).carrier;
        }

        if (!load?.ID) {
          toast({
            description: 'Load ID is required for updates.',
            variant: 'destructive',
          });
          return;
        }

        const res = await updateTMS(load.ID, {
          operation: TMSOperation.UpdateLoad,
          load: updateData,
        });

        if (res.isOk()) {
          toast({
            description: 'Load updated successfully!',
            variant: 'success',
          });
          await acceptLoadViewSuggestion(
            clickedSuggestion,
            updateData as Load,
            setCurrentState,
            undefined,
            tmsName
          );
        } else {
          toast({
            description: res.error.message || 'Error updating load.',
            variant: 'destructive',
          });
        }
      } catch {
        toast({
          description: 'Error updating load.',
          variant: 'destructive',
        });
      }
      setIsLoading(false);
    }

    // Call custom submit handler if provided
    if (onSubmit) {
      onSubmit(submissionData);
    }
  };

  const handleFormSubmit = async (data: NormalizedLoad) => {
    await processFormSubmit(data);
  };

  // Handle confirmed submission after user accepts duplicate load warning
  const handleConfirmedSubmit = async () => {
    const data = pendingSubmissionData ?? formMethods.getValues();

    try {
      await processFormSubmit(data, { bypassDuplicateCheck: true });
    } catch {
      // Handle any uncaught exceptions (e.g., 401 from createLocation, extension context invalidated)
      // Error is already logged by createLocation's captureException.
      toast({
        description: 'Error creating load. Please try again.',
        variant: 'destructive',
      });
    } finally {
      // Always reset loading state and close dialog, even on error
      setIsLoading(false);
      setShowDuplicateWarningDialog(false);
      setPendingSubmissionData(null);
    }
  };

  // Default onInvalid handler if none provided
  const defaultOnInvalid: SubmitErrorHandler<NormalizedLoad> = async (
    errors
  ) => {
    handleFormErrors(errors, {
      tmsName: TMS.Turvo,
      maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
      activeTabs,
      setActiveTabs,
      toast,
      routeStopCount: formMethods.getValues('stops')?.length ?? 0,
    });
  };

  const handleFormSubmitWrapper = formMethods.handleSubmit(
    handleFormSubmit,
    onInvalid || defaultOnInvalid
  );

  return (
    <div className='mb-5'>
      <ExtendedFormProvider
        aiDefaultValues={showAIHints}
        aiIconOnly={true}
        highlightDirtyFields={!isCreateMode}
      >
        <FormProvider {...formMethods}>
          <form onSubmit={handleFormSubmitWrapper}>
            <LoadBuildingAccordionControls
              activeTabs={activeTabs}
              allTabs={allTabs}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
              defaultView={defaultView}
              onDefaultViewChange={setDefaultView}
            />
            <Accordion
              type='multiple'
              value={activeTabs}
              onValueChange={setActiveTabs}
            >
              <LoadSectionAccordionItem
                label='Customer'
                icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.customer}
                activeTabs={activeTabs}
              >
                <CustomerSectionForm
                  formMethods={formMethods}
                  customers={mergedCustomers}
                  setCustomers={setCustomersAction}
                  isLoadingCustomers={isLoadingCustomers}
                  setIsLoadingCustomers={setIsLoadingCustomers}
                  customerSelectMountKey={turvoCustomerSelectMountKey}
                  originalSuggestionData={
                    clickedSuggestion?.suggested as NormalizedLoad
                  }
                  aiHighlightedExternalTMSID={customerExternalTMSIDString(
                    originalSuggestionCustomer
                  )}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Specs'
                icon={<Weight className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.specifications}
                activeTabs={activeTabs}
              >
                <SpecificationsForm
                  formMethods={formMethods}
                  specsSelectMountKey={turvoSpecsSelectMountKey}
                  isCreateMode={isCreateMode}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Rates'
                icon={
                  <CircleDollarSignIcon className='h-6 w-6' strokeWidth={1} />
                }
                name={AvailableTabs.rates}
                activeTabs={activeTabs}
              >
                <RatesForm formMethods={formMethods} />
              </LoadSectionAccordionItem>

              <TurvoRouteSection
                formMethods={formMethods}
                activeTabs={activeTabs}
                accordionName={AvailableTabs.route}
                allowStructureEdits={isCreateMode}
                isLoadingLocations={isLoadingLocations}
                locations={locations}
                handleRefreshLocations={handleRefreshLocations}
                tmsID={tmsIntegrations[0]?.id}
                setLocations={setLocations}
                locationSelectMountKey={turvoLocationSelectMountKey}
                originalSuggestionData={
                  clickedSuggestion?.suggested as Undef<NormalizedLoad>
                }
              />

              {showCarrierSection && (
                <LoadSectionAccordionItem
                  label='Carrier'
                  icon={<TruckIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.carrier}
                  activeTabs={activeTabs}
                >
                  {clickedSuggestion?.pipeline ===
                  SuggestionPipelines.CarrierInfo ? (
                    <>
                      <LoadTextInput
                        name='carrier.firstDriverName'
                        label='Driver Name'
                      />
                      <LoadTextInput
                        name='carrier.firstDriverPhone'
                        label='Driver Phone'
                      />
                      <LoadTextInput
                        name='carrier.truckNumber'
                        label='Truck #'
                      />
                      <LoadTextInput
                        name='carrier.trailerNumber'
                        label='Trailer #'
                      />
                      <LoadTextInput
                        name='carrier.dispatchCity'
                        label='Dispatch City'
                      />
                      <LoadTextInput
                        name='carrier.dispatchState'
                        label='Dispatch State'
                      />
                      <LoadTextInput
                        name='carrier.dispatchSource'
                        label='Dispatch Source'
                      />
                    </>
                  ) : (
                    externalLinks?.viewCarrier && (
                      <Flex
                        justify='between'
                        align='center'
                        className='p-4 bg-neutral-200 rounded'
                      >
                        <a
                          href={externalLinks.viewCarrier}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-accent-600 hover:text-accent-800 underline'
                        >
                          Edit carrier details on Turvo.
                        </a>
                        <ExternalLinkIcon className='w-3 h-3' />
                      </Flex>
                    )
                  )}
                </LoadSectionAccordionItem>
              )}

              {showOperatorSection && (
                <LoadSectionAccordionItem
                  label='Operator'
                  icon={<CircleUserRound className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.operator}
                  activeTabs={activeTabs}
                >
                  <OperatorSectionForm
                    formMethods={formMethods}
                    operators={operators}
                  />
                </LoadSectionAccordionItem>
              )}

              {showBillToSection && (
                <LoadSectionAccordionItem
                  label='Bill To'
                  icon={<ReceiptIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.billTo}
                  activeTabs={activeTabs}
                >
                  <BillToSectionForm />
                </LoadSectionAccordionItem>
              )}
            </Accordion>

            <section className='w-full mt-4'>
              <Button
                buttonNamePosthog={
                  isCreateMode
                    ? ButtonNamePosthog.BuildLoad
                    : ButtonNamePosthog.UpdateTMS
                }
                type='submit'
                className='w-full'
                disabled={isLoading}
                data-testid={
                  isCreateMode
                    ? 'create-load-submit'
                    : 'load-info-update-submit'
                }
                logProperties={{ serviceID, tmsName }}
              >
                {(() => {
                  if (isLoading) {
                    return <ButtonLoader />;
                  }
                  if (isCreateMode) {
                    return ButtonText.BuildLoad;
                  }
                  return ButtonText.UpdateTMS;
                })()}
              </Button>

              {isCreateMode && formState.isDirty && (
                <Flex justify='center' align='center'>
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.ClearForm}
                    type='button'
                    className='w-50% mt-4 h-8 text-sm text-neutral-500'
                    disabled={isLoading}
                    variant='outline'
                    onClick={handleClearForm}
                    logProperties={{ serviceID }}
                  >
                    {ButtonText.ClearForm}
                  </Button>
                </Flex>
              )}

              {isCreateMode && builtLoad && (
                <>
                  <LoadCreatedMessage
                    builtLoadId={builtLoad.externalTMSID}
                    poNumber={builtLoad.poNums?.trim() || undefined}
                    sharepointWriteStatus={sharepointWriteStatus}
                  />
                  <Typography
                    variant='body-sm'
                    className='mb-1 cursor-pointer underline'
                    data-testid='created-load-link'
                    onClick={() =>
                      openExternalUrl(
                        `https://app.turvo.com/#/${tmsTenant}/shipments/${builtLoad.externalTMSID}/details`
                      )
                    }
                  >
                    Access the created load for more details
                  </Typography>
                </>
              )}
            </section>
          </form>
        </FormProvider>
      </ExtendedFormProvider>

      <DuplicateLoadWarningDialog
        open={showDuplicateWarningDialog}
        onOpenChange={(open) => {
          setShowDuplicateWarningDialog(open);
          if (!open) {
            // Only reset loading state if not currently submitting.
            if (!isLoading) {
              setIsLoading(false);
              setPendingSubmissionData(null);
            }
          }
        }}
        onConfirm={handleConfirmedSubmit}
        isLoading={isLoading}
      />
    </div>
  );
}

// Placeholder components that will be implemented
